import React, { ChangeEvent, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Divider,
    Container,
    TextField,
    MenuItem
} from '@mui/material';
import {
    CloudUpload,
    Delete,
    Close,
    Campaign
} from '@mui/icons-material';

interface AdUploaderProps {
    preview: string | null;
    fileType: string | null;
    onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClearMedia: () => void;
    onSubmitAd?: (payload: {
        adUrl: string | null;
        lifetimeMinutes: number | null;
    }) => void;
    currentAd?: {
        ad_id: string;
        redirect_url?: string;
        lifetime_minutes?: number;
        file_path?: string;
    } | null;
    onDeleteAd?: (adId: string) => void;
    poppins: { fontFamily: string };
    isUploadingAd: boolean;
}

const AdUploader: React.FC<AdUploaderProps> = ({
                                                   preview,
                                                   fileType,
                                                   onImageChange,
                                                   onClearMedia,
                                                   onSubmitAd,
                                                   currentAd,
                                                   onDeleteAd,
                                                   poppins,
                                                   isUploadingAd
                                               }) => {

    const isEditMode = !!currentAd;

    const [externalUrl, setExternalUrl] = useState<string>('');
    const [lifetime, setLifetime] = useState<number | string>('infinite');

    useEffect(() => {
        if (currentAd) {
            setExternalUrl(currentAd.redirect_url || '');
            setLifetime(
                currentAd.lifetime_minutes ?? 'infinite'
            );
        } else {
            setExternalUrl('');
            setLifetime('infinite');
        }
    }, [currentAd]);

    const isVideo =
        fileType?.startsWith('video/') ||
        preview?.match(/\.(mp4|webm|mov)($|\?)/i);

    const handleAction = () => {
        const minutes =
            lifetime === 'infinite'
                ? null
                : Number(lifetime);

        console.log(
            "Button clicked!",
            "Mode:",
            isEditMode ? "UPDATE" : "CREATE"
        );

        if (onSubmitAd) {
            onSubmitAd({
                adUrl: externalUrl.trim() || null,
                lifetimeMinutes: minutes
            });
        }
    };

    return (
        <Container
            maxWidth="lg"
            sx={{
                mb: 10,
                pt: 5,
                pb: 20
            }}
        >
            <Box
                sx={{
                    border: '2px dashed #1B2559',
                    borderRadius: '20px',
                    p: 3,
                    bgcolor: '#FFFFFF',
                    minHeight: 280,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        mb: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Box
                            sx={{
                                p: 1.5,
                                bgcolor: '#E1E9F8',
                                borderRadius: '12px'
                            }}
                        >
                            <Campaign sx={{ color: '#1B2559' }} />
                        </Box>

                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: '#1B2559',
                                ...poppins
                            }}
                        >
                            {isEditMode
                                ? "Update Ad Campaign"
                                : "New Ad Campaign"}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {isEditMode && (
                            <Button
                                startIcon={<Close />}
                                color="inherit"
                                onClick={onClearMedia}
                                sx={{
                                    textTransform: 'none'
                                }}
                            >
                                Cancel
                            </Button>
                        )}

                        {isEditMode &&
                            onDeleteAd &&
                            currentAd && (
                                <Button
                                    startIcon={<Delete />}
                                    color="error"
                                    onClick={() =>
                                        onDeleteAd(
                                            currentAd.ad_id
                                        )
                                    }
                                >
                                    Delete
                                </Button>
                            )}
                    </Box>
                </Box>

                {/* Upload Section */}
                {!preview && !isEditMode ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexGrow: 1,
                            py: 4,
                            border: '1px solid #E0E5F2',
                            borderRadius: '16px'
                        }}
                    >
                        <Button
                            variant="text"
                            component="label"
                            startIcon={<CloudUpload />}
                            sx={{
                                ...poppins,
                                flexDirection: 'column',
                                color: '#1B2559',
                                textTransform: 'none'
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600
                                }}
                            >
                                Upload Ad Creative
                            </Typography>

                            <input
                                type="file"
                                hidden
                                accept="image/*,video/*"
                                onChange={onImageChange}
                            />
                        </Button>
                    </Box>
                ) : (
                    <Box
                        textAlign="center"
                        sx={{
                            width: '100%',
                            mb: 2
                        }}
                    >
                        {preview ? (
                            isVideo ? (
                                <video
                                    src={preview}
                                    controls
                                    style={{
                                        width: '100%',
                                        maxHeight: 350,
                                        borderRadius: '16px',
                                        backgroundColor: '#000'
                                    }}
                                />
                            ) : (
                                <img
                                    src={preview}
                                    alt="preview"
                                    style={{
                                        width: '100%',
                                        maxHeight: 220,
                                        borderRadius: '16px',
                                        objectFit: 'contain'
                                    }}
                                />
                            )
                        ) : (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#A3AED0'
                                }}
                            >
                                No preview available
                            </Typography>
                        )}

                        {!isEditMode && (
                            <Button
                                size="small"
                                color="error"
                                onClick={onClearMedia}
                                sx={{
                                    mt: 1,
                                    ...poppins
                                }}
                            >
                                Remove Asset
                            </Button>
                        )}
                    </Box>
                )}

                {/* Destination URL */}
                <TextField
                    fullWidth
                    label="Destination URL"
                    variant="outlined"
                    value={externalUrl}
                    onChange={(e) =>
                        setExternalUrl(e.target.value)
                    }
                    sx={{ mt: 3 }}
                />

                <Divider
                    sx={{
                        my: 2,
                        borderStyle: 'dashed'
                    }}
                />

                {/* Duration */}
                <TextField
                    select
                    fullWidth
                    label="Ad Duration"
                    value={lifetime}
                    onChange={(e) =>
                        setLifetime(e.target.value)
                    }
                    sx={{ mb: 3 }}
                >
                    <MenuItem value="infinite">
                        Run Indefinitely
                    </MenuItem>

                    <MenuItem value={1440}>
                        24 Hours
                    </MenuItem>

                    <MenuItem value={10080}>
                        7 Days
                    </MenuItem>
                </TextField>

                {/* Submit */}
                <Button
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={handleAction}
                    sx={{
                        bgcolor: '#1B2559',
                        py: 1.5,
                        fontWeight: 700,
                        '&:hover': {
                            bgcolor: '#2b3979'
                        },
                        ...poppins
                    }}
                >
                    {isUploadingAd
                        ? "Processing..."
                        : isEditMode
                            ? "Update Campaign"
                            : "Launch Campaign"}
                </Button>
            </Box>
        </Container>
    );
};

export default AdUploader;